import { Injectable } from "@angular/core";
import { Observable, from, of } from "rxjs";
import { catchError, concatMap, map, take, tap } from "rxjs/operators";

import { canvasToImage } from "app/_api/canvas/canvas-to-image";
import { loadPdf } from "app/_api/pdf/load-pdf";
import { pdfPageToCanvas } from "app/_api/pdf/pdf-page-to-canvas";
import { type PDFDocumentProxy } from "app/_api/pdf/pdfjs";
import { getPathValue } from "app/utils/get";
import {
  BranchOfferSettingsUpdateInput,
  BranchOfferTemplateSettingsUpdateInput,
} from "graphql/models/extended";
import {
  BranchCreateInput,
  BranchUpdateInput,
  OfferTemplateSettingsUpdateInput,
  BranchRoleMapping,
  Branch,
  OfferSettings,
  OfferTemplateSettings,
  DeleteOfferTemplatePdfResult,
  DeleteOfferTemplatePdfType,
  Organization,
  SftpCredentialsSetInput,
  SftpCredentialsResult,
  SftpCredentialWhereUniqueInput, SftpConnectionInput, SftpConnectionResult
} from 'graphql/models/generated';
import { GetSftpCredentialQuery } from "graphql/queries/sftp-credentials";
import { SftpValidateQuery } from "graphql/queries/sftp-validate";
import { DeleteOfferTemplatePdfMutation } from "graphql/mutations/delete-offer-template-pdf";
import { SetSftpCredentialMutation } from "graphql/mutations/set-sftp-credential";
import { RemoveSftpCredentialMutation } from "graphql/mutations/remove-sftp-credential";
import { AccountRole, CurrentAccountRoles } from "models/account.model";
import { OfferTemplateSettingsItemType } from "models/offer-template-settings.model";
import { MediaService } from "shared/services/media.service";
import { StoreService } from "shared/services/store.service";

import { CoreDataBuilderService } from "./core-data-builder.service";

@Injectable({
  providedIn: "root",
})
export class SettingsService {
  constructor(
    private mediaService: MediaService,
    private storeService: StoreService,
    private builderService: CoreDataBuilderService,
    private deleteOfferTemplatePdfMutation: DeleteOfferTemplatePdfMutation,
    private setSftpCredentialMutation: SetSftpCredentialMutation,
    private removeSftpCredentialMutation: RemoveSftpCredentialMutation,
    private getSftpCredentialQuery: GetSftpCredentialQuery,
    private sftpValidateQuery: SftpValidateQuery
  ) {}

  getBranch(id: string): Observable<Branch> {
    const where = { id };
    const variables = { where };

    return this.builderService.getOne<Branch>("Branch", variables);
  }

  getBranchBase(id: string): Observable<Branch> {
    const where = { id };
    const variables = { where };

    return this.builderService.getOne<Branch>(
      "Branch",
      variables,
      "BranchBaseFragment"
    );
  }

  getBranchLogo(id: string): Observable<Branch> {
    const where = { id };
    const variables = { where };

    return this.builderService.getOne<Branch>(
      "Branch",
      variables,
      "BranchImageFragment"
    );
  }

  getPdfTemplates(id: string): Observable<Branch> {
    const where = { id };
    const variables = { where };

    return this.builderService.getOne<Branch>(
      "Branch",
      variables,
      "BranchImageFragment"
    );
  }

  getBranches(): Observable<Branch[]> {
    const variables = {};

    return this.builderService.getMany<Branch>("Branch", variables)
      .pipe(map(branches => branches.filter(b => !b.missingInSource)));
  }

  getOrganizations(): Observable<Organization[]> {
    const variables = {};

    return this.builderService.getMany<Organization>("Organization", variables)
      .pipe(map(orgs => orgs.filter(org => !org.missingInSource)));
  }

  filterBranchesByRoleNotUser(
    branches: Branch[],
    roles: CurrentAccountRoles
  ): Branch[] {
    return branches.filter((branch: Branch) =>
      Boolean(
        roles.branchRoles.find(
          (role: BranchRoleMapping) =>
            role.branchId === branch.id && role.role !== AccountRole.USER
        )
      )
    );
  }

  updateBranchWithLogo(
    id: string,
    formValue: Record<string, any>,
    uploadUrl?: string
  ): Observable<Branch> {
    const formsValue = { ...formValue };
    const photo: File = formsValue.photo;
    delete formsValue.photo;

    return this.updateBranch(id, formsValue).pipe(
      concatMap((branch: Branch): Observable<Branch> => {
        if (photo && uploadUrl) {
          return this.mediaService.uploadFile(uploadUrl, photo).pipe(
            tap(() => this.storeService.setCurrentAccountBranch({ ...branch })),
            map(() => branch)
          );
        }

        return of(branch);
      })
    );
  }

  updateBranch(id: string, formValue: Record<string, any>): Observable<Branch> {
    const values = { ...formValue };
    const data: BranchUpdateInput = this.builderService.buildDataInput(
      values,
      false
    );
    return this.builderService.update<Branch, BranchUpdateInput>(
      "Branch",
      id,
      data
    );
  }

  createBranchWithLogo(formValue: Record<string, any>): Observable<any> {
    const formsValue = { ...formValue };
    const photo: File = formsValue.photo;
    delete formsValue.photo;

    return this.createBranch(formsValue).pipe(
      concatMap((branch: Branch): Observable<Branch> => {
        if (photo && branch.id && !branch.imageUrls) {
          return this.getBranchLogo(branch.id).pipe(
            concatMap((branchPhoto: Branch): Observable<Branch> => {
              const branchWithImage: Branch = {
                ...branch,
                imageUrls: branchPhoto.imageUrls,
              };
              if (
                photo &&
                branchWithImage.imageUrls &&
                branchWithImage.imageUrls.upload
              ) {
                return this.mediaService
                  .uploadFile(branchWithImage.imageUrls.upload, photo)
                  .pipe(map(() => branchWithImage));
              }

              return of(branchWithImage);
            })
          );
        }

        return of(branch);
      })
    );
  }

  createBranch(formValue: Record<string, any>): Observable<Branch> {
    const values = { ...formValue };

    return this.builderService.create<Branch, BranchCreateInput>(
      "Branch",
      values
    );
  }

  updateOfferSettings(
    branchId: string,
    formValue: Record<string, any>
  ): Observable<OfferSettings> {
    const data: any = {};

    let createSection: any = {
      minMargin: formValue.minMargin,
      fileExportStructureType: formValue.exportType,
    };
    let updateSection: any = {
      minMargin: { set: Number(formValue.minMargin) },
      fileExportStructureType: { set: formValue.exportType },
    };

    if (formValue.prefix1) {
      const { type, value } = formValue.prefix1;
      createSection.prefix1 = {
        create: {
          type,
          value,
        },
      };

      updateSection.prefix1 = {
        upsert: {
          create: {
            type: type,
            value: value,
          },
          update: {
            type: {
              set: type,
            },
            value: {
              set: value,
            },
          },
        },
      };
    }

    if (formValue.prefix2) {
      const { type, value } = formValue.prefix2;
      createSection.prefix2 = {
        create: {
          type,
          value,
        },
      };

      updateSection.prefix2 = {
        upsert: {
          create: {
            type: type,
            value: value,
          },
          update: {
            type: {
              set: type,
            },
            value: {
              set: value,
            },
          },
        },
      };
    }

    if (formValue.prefix3) {
      const { type, value } = formValue.prefix3;
      createSection.prefix3 = {
        create: {
          type,
          value,
        },
      };

      updateSection.prefix3 = {
        upsert: {
          create: {
            type: type,
            value: value,
          },
          update: {
            type: {
              set: type,
            },
            value: {
              set: value,
            },
          },
        },
      };
    }

    if (formValue.suffix) {
      const { type, value } = formValue.suffix;
      createSection.suffix = {
        create: {
          type: type,
          value: value,
        },
      };

      updateSection.suffix = {
        upsert: {
          create: {
            type: type,
            value: value,
          },
          update: {
            type: {
              set: type,
            },
            value: {
              set: value,
            },
          },
        },
      };
    }

    data.create = createSection;
    data.update = updateSection;

    return this.builderService
      .update<Branch, BranchOfferSettingsUpdateInput>(
        "Branch",
        branchId,
        { offerSettings: { upsert: data } },
        "BranchOfferSettingsFragment"
      )
      .pipe<OfferSettings>(
        map(
          (updatedBranch: Branch) =>
            updatedBranch.offerSettings as OfferSettings
        )
      );
  }

  private uploadFile(
    offerTemplateSettings: OfferTemplateSettings,
    file: File | Blob,
    path: string
  ): Observable<OfferTemplateSettings> {
    if (file) {
      const uploadUrl = getPathValue(offerTemplateSettings, path);
      if (uploadUrl) {
        return this.mediaService
          .uploadFile(uploadUrl, file)
          .pipe(map(() => offerTemplateSettings));
      }
    }

    return of(offerTemplateSettings);
  }

  private buildAndUploadOverlayPDF(
    offerTemplateSettings: OfferTemplateSettings,
    fileAndPdf: [File, PDFDocumentProxy],
    type:
      | OfferTemplateSettingsItemType.PDF_FIRST_PAGE
      | OfferTemplateSettingsItemType.PDF_FOLLOWING_PAGES,
    backgroundPdf?: string // = '/assets/common/pdf/offer-preview-background.pdf'
  ): Observable<OfferTemplateSettings> {
    if (Array.isArray(fileAndPdf) && fileAndPdf.length === 2) {
      const [file, pdf] = fileAndPdf;
      if (file && pdf) {
        // get proper settings
        let pathKey = "";
        let bgPdfPageNumber = 1;

        switch (type) {
          case OfferTemplateSettingsItemType.PDF_FIRST_PAGE:
            pathKey = "pdfFirstPage";
            bgPdfPageNumber = 1;
            break;

          case OfferTemplateSettingsItemType.PDF_FOLLOWING_PAGES:
            pathKey = "pdfFollowingPages";
            bgPdfPageNumber = 2;
            break;

          default:
            // This fails on build due to the stone age versions used...
            //   ((_: never) => {
            //     // exhaustiveness check
            //   })(type);
            break;
        }

        // upload pdf
        return this.uploadFile(
          offerTemplateSettings,
          file,
          `${pathKey}.imageUrls.upload`
        ).pipe(
          // load offer preview w/o header & footer
          concatMap(() =>
            backgroundPdf ? from(loadPdf(backgroundPdf)) : of(undefined)
          ),
          // render offer preview w/o header & footer to canvas
          concatMap((pdfBackground) =>
            pdfBackground
              ? from(
                  pdfPageToCanvas({
                    pdf: pdfBackground,
                    pageNumber: bgPdfPageNumber,
                  })
                )
              : of(undefined)
          ),
          // render the customer's pdf overlay on top of the offer preview w/o header & footer
          concatMap((bgCanvas) =>
            from(
              pdfPageToCanvas({
                pdf,
                pageNumber: 1,
                canvasContext: bgCanvas,
                background: "rgba(0,0,0,0)",
              })
            )
          ),
          // convert the result to an image
          concatMap((previewCanvas) =>
            previewCanvas
              ? from(canvasToImage(previewCanvas.canvas))
              : of(undefined)
          ),
          // upload preview image
          concatMap((previewFile) => {
            if (previewFile) {
              return this.uploadFile(
                offerTemplateSettings,
                previewFile,
                `${pathKey}Preview.imageUrls.upload`
              );
            }

            return of(offerTemplateSettings);
          })
        );
      }
    }

    return of(offerTemplateSettings);
  }

  /**
   * Update branch text data first, then check/upload individual files sequentially.
   */
  updateOfferTemplateSettings(
    branchId: string,
    formValue: Record<string, any>,
    createPdfFirstPage = false,
    createPdfFollowingPages = false
  ): Observable<Branch> {
    const {
      file: logoImage,
      footerImage,
      pdfFirstPage,
      pdfFollowingPages,
      ...formValues
    } = formValue;

    const data: OfferTemplateSettingsUpdateInput =
      this.builderService.buildDataInput(formValues, false);
    // build data input
    if (logoImage) {
      data.logo = {
        update: {
          type: { set: OfferTemplateSettingsItemType.CUSTOM_IMAGE },
          text: { set: "" },
        },
      };
    }

    if (formValue.footer) {
      const { type, value } = formValue.footer;
      data.footer = {
        update: {
          type: { set: type },
          text: { set: value },
        },
      };
    }

    if (pdfFirstPage) {
      if (createPdfFirstPage === true) {
        data.pdfFirstPage = {
          create: {
            type: OfferTemplateSettingsItemType.PDF_FIRST_PAGE,
            text: "",
          },
        };
        data.pdfFirstPagePreview = {
          create: {
            type: OfferTemplateSettingsItemType.PDF_FIRST_PAGE_PREVIEW,
            text: "",
          },
        };
      } else {
        data.pdfFirstPage = {
          update: {
            type: { set: OfferTemplateSettingsItemType.PDF_FIRST_PAGE },
            text: { set: "" },
          },
        };
        data.pdfFirstPagePreview = {
          update: {
            type: { set: OfferTemplateSettingsItemType.PDF_FIRST_PAGE_PREVIEW },
            text: { set: "" },
          },
        };
      }
    }

    if (pdfFollowingPages) {
      if (createPdfFollowingPages === true) {
        data.pdfFollowingPages = {
          create: {
            type: OfferTemplateSettingsItemType.PDF_FOLLOWING_PAGES,
            text: "",
          },
        };
        data.pdfFollowingPagesPreview = {
          create: {
            type: OfferTemplateSettingsItemType.PDF_FOLLOWING_PAGES_PREVIEW,
            text: "",
          },
        };
      } else {
        data.pdfFollowingPages = {
          update: {
            type: { set: OfferTemplateSettingsItemType.PDF_FOLLOWING_PAGES },
            text: { set: "" },
          },
        };
        data.pdfFollowingPagesPreview = {
          update: {
            type: {
              set: OfferTemplateSettingsItemType.PDF_FOLLOWING_PAGES_PREVIEW,
            },
            text: { set: "" },
          },
        };
      }
    }

    return this.builderService
      .update<Branch, BranchOfferTemplateSettingsUpdateInput>(
        "Branch",
        branchId,
        { offerTemplateSettings: { update: data } },
        "BranchOfferTemplateSettingsFragment"
      )
      .pipe(
        map(
          (updatedBranch: Branch) =>
            updatedBranch.offerTemplateSettings as OfferTemplateSettings
        ),
        concatMap((settings: OfferTemplateSettings) =>
          this.uploadFile(settings, logoImage, "logo.imageUrls.upload")
        ),
        concatMap((settings: OfferTemplateSettings) =>
          this.uploadFile(settings, footerImage, "footer.imageUrls.upload")
        ),
        concatMap((settings: OfferTemplateSettings) =>
          this.buildAndUploadOverlayPDF(
            settings,
            pdfFirstPage,
            OfferTemplateSettingsItemType.PDF_FIRST_PAGE
          )
        ),
        concatMap((settings: OfferTemplateSettings) =>
          this.buildAndUploadOverlayPDF(
            settings,
            pdfFollowingPages,
            OfferTemplateSettingsItemType.PDF_FOLLOWING_PAGES
          )
        ),
        concatMap(() => this.getBranch(branchId))
      );
  }

  deletePdf(
    branchId: string,
    type: DeleteOfferTemplatePdfType
  ): Observable<DeleteOfferTemplatePdfResult | undefined> {
    return this.deleteOfferTemplatePdfMutation
      .mutate({ data: { branchId, type } }, { fetchPolicy: "no-cache" })
      .pipe(
        map((response) => response.data?.deleteOfferTemplatePdf),
        take(1),
        catchError((error) => {
          console.error("Error on deletePdf:", error);

          return of(undefined);
        })
      );
  }

  getSftpCredential(
    branchId?: string
  ): Observable<SftpCredentialsResult> {
    const requestData$ = (
      branchId ? of(branchId) : this.storeService.getCurrentAccountBranch().pipe(
        map((branch) => branch.id),
        take(1)
      )
    );

    return requestData$.pipe(
      concatMap((branchId) => {
        const where: SftpCredentialWhereUniqueInput = {
          sftpCredentialUniqueId: {
            branchId
          }
        };

        const variables = { where };

        return this.getSftpCredentialQuery.fetch(variables, { fetchPolicy: 'no-cache' })
          .pipe<SftpCredentialsResult, SftpCredentialsResult>(
            map((r) => r.data?.getSftpCredential as SftpCredentialsResult),
            take(1)
          );
      })
    )
  }

  setSftpCredentials(
    variables: SftpCredentialsSetInput & Record<string, unknown>,
    branchId?: string
  ): Observable<SftpCredentialsResult> {

    const requestData$ = (
      branchId ? of(branchId) : this.storeService.getCurrentAccountBranch().pipe(
        map((branch) => branch.id),
        take(1)
      )
    );

    const { branchId: formBranchId, host, password, port, remotePath, username, valid } = variables;

    const vars: SftpCredentialsSetInput = {
      branchId: formBranchId,
      host,
      password,
      port: Number(port),
      remotePath: remotePath && remotePath.charAt(0) === '/' ? remotePath.substring(1) : remotePath,
      username,
      valid,
    };

    return requestData$.pipe(
      concatMap((branchId) => {
        vars.branchId = branchId;

        const data = { data: vars };

        return this.setSftpCredentialMutation.mutate(data, { fetchPolicy: 'no-cache' })
        .pipe<SftpCredentialsResult, SftpCredentialsResult>(
          map((r) => r.data?.setSftpCredential as SftpCredentialsResult),
          take(1)
        );
      })
    )
  }

  deleteSftpCredential(
    branchId?: string
  ): Observable<any> {
    const branchData$ = (
      branchId ? of(branchId) : this.storeService.getCurrentAccountBranch().pipe(
        map((branch) => branch.id),
        take(1)
      )
    );

    return branchData$.pipe(
      concatMap((branchId) => {
        const where: SftpCredentialWhereUniqueInput = {
          sftpCredentialUniqueId: {
            branchId
          }
        };

        const variables = { where };

        return this.removeSftpCredentialMutation.mutate(variables, { fetchPolicy: 'no-cache' })
        .pipe<SftpCredentialsResult, SftpCredentialsResult>(
          map((r) => r.data?.setSftpCredential as SftpCredentialsResult),
          take(1)
        );
      })
    )
  }

  validateSftpConnection(data: SftpConnectionInput | null) {
    const variables = { data: data };

    return this.sftpValidateQuery.fetch(variables, { fetchPolicy: 'no-cache' })
    .pipe<SftpConnectionResult, SftpConnectionResult>(
      map((r) => r.data?.validateSftp as SftpConnectionResult),
      take(1)
    );
  }

}
